<template>
  <a-select
    show-search
    :mode="mode"
    v-model="associatesProxy"
    :autoClearSearchValue="false"
    :allow-clear="true"
    :filter-option="false"
    :show-action="openOnFocus ? ['click', 'focus'] : undefined"
    :disabled="disabled"
    :placeholder="loadingAssociated || loadingDefault ? 'Loading...' : placeholder"
    @search="handleSearch"
    @focus="focusAssociates"
    @blur="blurAssociates"
    @dropdownVisibleChange="handleDropdownVisibleChange"
  >
    <a-select-option
      v-for="user in allUsers"
      :key="getUserKey(user)"
      :disabled="disabledOptions.includes(getUserKey(user)) || (!canSelectMore && !isCurrentlySelected(user))"
      :value="getUserKey(user)"
    >
      <a-tooltip placement="left">
        <template #title>
          <span>{{ getTagStatus(user.work_status, user.termination?.type).title }}</span>
        </template>
        <a-badge :color="getTagStatus(user.work_status, user.termination?.type).color" />
      </a-tooltip>
      {{ `[${user.associate_number}] ${user.first_name} ${user.last_name}` }}
    </a-select-option>
    <template slot="dropdownRender" slot-scope="menu">
      <slot name="dropdown-before-menu" :query="searchQuery" />
      <a-divider v-if="hasDropdownBeforeMenu" style="margin: 0" />
      <slot name="associates-heading" />
      <div style="padding: 5px 12px" @mousedown.prevent @click.prevent.stop="toggleIncludeDisabled">
        <a-checkbox :checked="includeDisabled"> Include Disabled </a-checkbox>
      </div>
      <a-divider style="margin: 0" />
      <slot name="dropdown" />
      <a-spin tip="Loading..." :spinning="loadingAssociated">
        <v-nodes :vnodes="menu" />
      </a-spin>
    </template>
    <template v-if="hasNotFoundContent" #notFoundContent>
      <slot name="not-found-content" />
    </template>
  </a-select>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import { getAssociateStatus } from '@/store/modules/profile/utils'
import VNodes from '@/components/ui/v-nodes.vue'
import { Badge, Checkbox, Divider, notification, Select, Spin, Tooltip } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'

const { mapActions: searchActions } = createNamespacedHelpers('search')
const { mapActions: profileActions } = createNamespacedHelpers('profile')

export default {
  components: {
    'a-select': Select,
    'a-select-option': Select.Option,
    'a-badge': Badge,
    'a-checkbox': Checkbox,
    'a-tooltip': Tooltip,
    'a-divider': Divider,
    'a-spin': Spin,
    VNodes
  },
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    disabledOptions: {
      type: Array,
      default: () => []
    },
    multiply: {
      type: Boolean,
      default: false
    },
    value: {
      type: [String, Array, Number],
      default: undefined
    },
    isAssociateId: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Object,
      default: undefined
    },
    maxCount: {
      type: Number,
      default: null
    },
    openOnFocus: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Associate'
    }
  },
  data() {
    return {
      loadingAssociated: false,
      loadingDefault: false,
      searchQuery: '',
      includeDisabled: false,

      allUsers: [],
      associates: [],

      requestKey: `associate-search-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    }
  },
  watch: {
    searchQuery: {
      handler() {
        this.fetchAssociates()
      }
    },
    filter: {
      handler() {
        this.fetchAssociates()
      },
      immediate: true,
      deep: true
    },
    includeDisabled: {
      handler() {
        this.fetchAssociates()
      }
    },
    value: {
      handler(newVal) {
        const needFetch = this.multiply ? newVal?.length > 0 : !!newVal

        if (!needFetch) {
          return
        }

        const alreadyExist = this.multiply
          ? (newVal ?? []).every(associate =>
              this.allUsers.some(item =>
                this.isAssociateId ? item.id === associate : item.associate_number === associate
              )
            )
          : this.allUsers.some(item => (this.isAssociateId ? item.id === newVal : item.associate_number === newVal))

        if (!alreadyExist) {
          this.fetchDefaultAssociates()
        }
      },
      deep: true,
      immediate: true
    }
  },
  computed: {
    mode() {
      return this.multiply ? 'multiple' : 'default'
    },

    canSelectMore() {
      if (!this.multiply || this.maxCount == null) return true
      const currentCount = Array.isArray(this.value) ? this.value.length : 0
      return currentCount < this.maxCount
    },

    hasDropdownBeforeMenu() {
      return Boolean(this.$scopedSlots['dropdown-before-menu'] || this.$slots['dropdown-before-menu'])
    },

    hasNotFoundContent() {
      return Boolean(this.$scopedSlots['not-found-content'] || this.$slots['not-found-content'])
    },

    associatesProxy: {
      get() {
        if (this.loadingDefault) {
          return undefined
        }
        if (this.isAssociateId) {
          if (Array.isArray(this.value)) {
            return this.value.map(Number)
          }
          return this.value ? Number(this.value) : undefined
        }
        return this.value || undefined
      },
      set(value) {
        if (value === undefined) {
          this.searchQuery = ''
        }

        this.$emit('update:value', value)
      }
    }
  },
  methods: {
    ...searchActions(['getUsers']),
    ...profileActions(['getAssociatesById']),

    isCurrentlySelected(user) {
      const key = this.getUserKey(user)
      if (!this.value) return false
      if (Array.isArray(this.value)) {
        return this.value.includes(key)
      }
      return this.value === key
    },

    getUserKey(user) {
      return this.isAssociateId ? user.id : user.associate_number
    },

    toggleIncludeDisabled() {
      this.includeDisabled = !this.includeDisabled
    },

    getTagStatus(work_status, terminationType) {
      return getAssociateStatus(work_status, terminationType)
    },

    showNotification(type, message, description) {
      notification[type]({
        message,
        description
      })
    },

    handleSearch(value) {
      this.$emit('search-input', value)
      this.queueAssociateSearch(value)
    },

    queueAssociateSearch: debounce(function (value) {
      this.searchQuery = value
    }, 1000),

    focusAssociates() {
      this.searchQuery = ''
      this.$emit('search-input', '')
    },

    blurAssociates() {
      this.searchQuery = ''
      this.$emit('search-input', '')
    },

    handleDropdownVisibleChange(open) {
      this.$emit('dropdown-visible-change', open)
    },

    // async calls

    async fetchAssociates() {
      this.loadingAssociated = true

      let skipFinally = false

      try {
        const data = await this.getUsers({
          query: this.searchQuery,
          limit: 10,
          filter: {
            ...this.filter,
            status: this.includeDisabled
              ? ['active', 'suspended', 'terminated', 'disabled']
              : ['active', 'suspended', 'terminated']
          },
          requestGroup: this.requestKey
        })
        if (this.associates.length) {
          const associateIds = new Set(this.associates.map(item => item.id))

          this.allUsers = [...this.associates, ...data.filter(item => !associateIds.has(item.id))]
        } else {
          this.allUsers = [...data]
        }
      } catch (error) {
        if (error === 'canceled') {
          skipFinally = true
          this.loadingAssociated = true
          return
        }
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
      } finally {
        if (!skipFinally) this.loadingAssociated = false
      }
    },

    async fetchDefaultAssociates() {
      this.loadingDefault = true

      const payload = {}

      if (this.isAssociateId) {
        payload.associates = this.multiply || Array.isArray(this.value) ? this.value : [this.value]
      } else {
        payload.associate_numbers = this.multiply || Array.isArray(this.value) ? this.value : [this.value]
      }

      try {
        const data = await this.getAssociatesById(payload)

        this.associates = data

        if (this.associates.length) {
          const associateIds = new Set(this.associates.map(item => item.id))

          this.allUsers = [...this.associates, ...this.allUsers.filter(item => !associateIds.has(item.id))]
        }
      } catch (error) {
        this.showNotification('error', 'Error', typeof error === 'string' ? error : error?.message)
        this.associates = []
      } finally {
        this.loadingDefault = false
      }
    }
  }
}
</script>

<style>
.ant-tooltip {
  z-index: 11000 !important;
}
</style>
